import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect, useMemo} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import useSelectorsById, {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {MaximumTableHeight, TableColumnType} from 'notebook/tables/TableConstants';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {CreateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';
import {DefaultStatusDisplayName, ProcessTableLocation} from 'processes/ProcessConstants';
import {
  findValidOutStatuses,
  getNextCompletedStatus,
  getStatusFromId,
  sortTaskStatuses,
} from 'processes/status/TaskStatusUtil';
import {SubmitOutputsConfirmationQuery} from 'processes/tables/__generated/SubmitOutputsConfirmationQuery';
import createReadOnlyProcessTable from 'processes/tables/createReadOnlyProcessTable';
import {createTaskTableFromTasks} from 'processes/tables/createTaskTable';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import {assertNever} from 'util/Types';

import {createOutputTableWithOptionalProcess} from '../../tables/createOutputTable';
import ProcessTaskStructuredTableAdapter from '../../tables/ProcessTaskStructuredTableAdapter';

type ProcessTask = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processTasks'][number],
  'ProcessTaskNode'
>;

interface CreateOutputsConfirmationPaneProps {
  tasks: Array<ProcessTask>;
  onUpdateTasks: (tasks: Array<ProcessTask>) => void;
  outputsBeingCreated: Array<CreateServerProcessOutput>;
  onChangeAreQueriesCompleted: (isSubmitEnabled: boolean) => void;
}

function buildOutputTable(
  outputsBeingCreated: Array<CreateServerProcessOutput>,
  tasks: Array<ProcessTask>,
  selectorsById: SchemaFieldSelectorsDict
): ProcessOutputStructuredTable {
  assert(tasks.length > 0, 'Expected at least one task');
  const processTaskSchema = tasks[0].schema;
  const tasksById = tasks.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as {[id: string]: ProcessTask}
  );

  const outputs = outputsBeingCreated.map((output) => ({
    task: {
      id: output.processTaskId,
      humanId: tasksById[output.processTaskId].humanId,
    },
    fields: processTaskSchema.outputSchema!.fields.map((field: SchemaField) => ({
      tagSchemaField: field,
      name: field.name,
      ...output.fields?.[field.name],
    })),
    archiveRecord: null,
    isPersisted: false,
  }));

  const outputTable = createOutputTableWithOptionalProcess({
    outputs,
    processTaskSchema: {
      ...processTaskSchema,
      outputSchema: processTaskSchema.outputSchema!,
    },
    metadata: {tableLocation: ProcessTableLocation.STATUS_CONFIRMATION_MODAL},
    selectorsById,
  });

  return createReadOnlyProcessTable<ProcessOutputStructuredTable>(outputTable, selectorsById);
}

function buildTaskTable(
  tasks: Array<ProcessTask>,
  selectorsById: SchemaFieldSelectorsDict
): ProcessTaskStructuredTable {
  const table = createTaskTableFromTasks({
    tasks,
    tableLocation: ProcessTableLocation.STATUS_CONFIRMATION_MODAL,
    selectorsById,
    locked: true,
    editableColumns: [TableColumnType.PROCESS_TASK_STATUS],
  });
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();

  return {
    ...table,
    rows: table.rows.map((row, rowIdx) => ({
      ...row,
      ...adapter.getRowWithNewStatus(
        table.columns,
        row,
        getNextCompletedStatus(tasks[rowIdx].status, tasks[rowIdx].schema.statusLifecycle) ??
          tasks[rowIdx].status
      ),
      isFinalized: false,
    })),
  };
}

function CreateOutputsConfirmationPaneWithoutTable(props: CreateOutputsConfirmationPaneProps): JSX.Element {
  assert(props.tasks.length > 0, 'Expected at least one task');
  const {initialStatus, availableStatuses} = useMemo(() => {
    assert(
      props.tasks.every((task) => task.status.id === props.tasks[0].status.id),
      'Expected all tasks to have the same status'
    );
    const initialStatus = props.tasks[0].status;
    const availableStatuses = sortTaskStatuses(
      findValidOutStatuses(initialStatus, props.tasks[0].schema.statusLifecycle)
    );
    return {initialStatus, availableStatuses};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedStatus, setSelectedStatus] = useState<ProcessTaskStatus>(
    getNextCompletedStatus(initialStatus, props.tasks[0].schema.statusLifecycle) ?? initialStatus
  );

  useEffect(() => {
    props.onChangeAreQueriesCompleted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    props.onUpdateTasks(
      props.tasks.map((task) => ({
        ...task,
        status: selectedStatus,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus.id]);

  const getInfoBannerModifiers = (): {willBeEditable: boolean; willBeMapped: boolean} => {
    switch (selectedStatus.statusType) {
      case ProcessTaskStatusType.COMPLETED:
      case ProcessTaskStatusType.SKIPPED:
        return {willBeEditable: false, willBeMapped: true};
      case ProcessTaskStatusType.IN_PROGRESS:
      case ProcessTaskStatusType.PENDING:
        return {willBeEditable: true, willBeMapped: false};
      case ProcessTaskStatusType.FAILED:
      case ProcessTaskStatusType.CANCELLED:
      case ProcessTaskStatusType.INVALID:
        return {willBeEditable: false, willBeMapped: false};
      default:
        assertNever(selectedStatus.statusType);
        return {willBeEditable: true, willBeMapped: false};
    }
  };

  const {willBeEditable, willBeMapped} = getInfoBannerModifiers();

  return (
    <>
      <div>
        When you submit these outputs, we will update the status for {props.tasks.length}{' '}
        {pluralize(props.tasks.length, 'task')}
      </div>
      <div className={css(styles.radioButtonWrapper)}>
        <RadioButtonGroup
          title="Update all task statuses to:"
          options={availableStatuses.map((status) => ({
            value: status.id,
            label: status.displayName,
          }))}
          selectedValue={selectedStatus.id}
          onChange={(value) => {
            setSelectedStatus(getStatusFromId(value, props.tasks[0].schema.statusLifecycle)!);
          }}
          isVertical={true}
          hasBottomSpacing={false}
        />
      </div>

      <InlineBanner
        bannerType={InlineBannerType.INFO}
        hideIcon={true}
        supportingInfo={
          <div>
            <ul className={css(styles.bannerText)}>
              <li>
                You <strong>{willBeEditable ? 'will' : "won't"}</strong> be able to change statuses or add new
                outputs to these tasks
              </li>
              <li>
                Tasks and outputs in a flowchart <strong>{willBeMapped ? 'will' : "won't"}</strong> be mapped
                to the next step
              </li>
            </ul>
          </div>
        }
      />
    </>
  );
}

function CreateOutputsConfirmationPaneWithTableContents(
  props: CreateOutputsConfirmationPaneProps & {selectorsById: SchemaFieldSelectorsDict}
): JSX.Element {
  const [taskTable, setTaskTable] = useState(buildTaskTable(props.tasks, props.selectorsById));
  const tasksById = props.tasks.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as {[id: string]: ProcessTask}
  );

  useEffect(() => {
    props.onUpdateTasks(
      taskTable.rows.map((row) => {
        const taskId = adapter.getTaskId(taskTable.columns, row)!;
        return {
          ...tasksById[taskId],
          status: getStatusFromId(
            adapter.getStatus(taskTable.columns, row)!.id,
            tasksById[taskId].schema.statusLifecycle
          )!,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskTable]);

  const readOnlyOutputTable = buildOutputTable(props.outputsBeingCreated, props.tasks, props.selectorsById);
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();

  return (
    <>
      <div>
        Tasks will be updated to a terminal status upon confirmation. You won't be able to further modify task
        statuses or create additional outputs for these tasks after submitting this update.
        <br />
        <br />
        Any outputs or tasks that are part of a flowchart will be mapped to the next step when the task status
        is set to {DefaultStatusDisplayName.COMPLETED}.
      </div>
      <div className={css(styles.tableWrapper)}>
        <StructuredTableOutsideNotebook
          structuredTable={taskTable}
          cid={taskTable.cid}
          updateHandler={(table: ProcessTaskStructuredTable) => {
            setTaskTable(table);
          }}
          locked={false}
          hasPrimarySamples={false}
          hideLeftHeaderControls={true}
          enableFormulas={true}
          maximumHeight={MaximumTableHeight.AUTO_FIXED}
        />
        <StructuredTableOutsideNotebook
          structuredTable={readOnlyOutputTable}
          cid={readOnlyOutputTable.cid}
          updateHandler={noop}
          locked={true}
          hasPrimarySamples={false}
          hideLeftHeaderControls={true}
          enableFormulas={true}
          maximumHeight={MaximumTableHeight.AUTO_FIXED}
        />
      </div>
    </>
  );
}

function CreateOutputsConfirmationPaneWithTable(props: CreateOutputsConfirmationPaneProps): JSX.Element {
  const selectorsById = useSelectorsById({includeArchivedSelectors: true});

  useEffect(() => {
    props.onChangeAreQueriesCompleted(
      selectorsById !== AsyncStatus.LOADING && selectorsById !== AsyncStatus.ERROR
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorsById]);

  if (selectorsById === AsyncStatus.LOADING) {
    return <CenteredHorizontalThrobber />;
  } else if (selectorsById === AsyncStatus.ERROR || selectorsById === AsyncStatus.NOT_FETCHED) {
    return <div>{GENERIC_ERROR_MESSAGE}</div>;
  }

  return <CreateOutputsConfirmationPaneWithTableContents {...props} selectorsById={selectorsById} />;
}

export function usesPaneWithoutTableForOutputCreation({
  outputsBeingCreated,
}: {
  outputsBeingCreated: Array<CreateServerProcessOutput>;
}): boolean {
  return outputsBeingCreated.length > BenchlingConfig.WORKFLOWS_MAX_ROWS_FOR_LEGACY_CONFIRMATION_MODALS;
}

export default function CreateOutputsConfirmationPane(
  props: CreateOutputsConfirmationPaneProps
): JSX.Element {
  return usesPaneWithoutTableForOutputCreation(props) ? (
    <CreateOutputsConfirmationPaneWithoutTable {...props} />
  ) : (
    <CreateOutputsConfirmationPaneWithTable {...props} />
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    marginTop: '8px',
    marginRight: '16px',
  },
  bannerText: {
    marginBlockEnd: 0,
  },
  radioButtonWrapper: {
    marginTop: '16px',
    marginBottom: '16px',
  },
});
